import { useQuery } from '@tanstack/react-query';

import { apiFetch, ApiRequestError } from '@/lib/api/client';
import { clearTokens, readAccessToken, storeTokens } from '@/lib/auth/token';

/**
 * 이 앱에 들어오는 주체.
 *
 * <p>협력사·감리·발주처는 `external_users` 에서, 직원(`STAFF`)은 `auth_users` 에서 온다(DOC-87 §3-1).
 * 직원은 외부 표에 복사되지 않는다 — 계정은 하나, 비밀번호도 하나다.
 */
export type PartyType = 'VENDOR' | 'SUPERVISOR' | 'OWNER' | 'STAFF';

/** 판정하는 손 — 감리와 직원. 검측 착수·판정, NCR 발행·확인, 안전 지적·확인이 여기 걸린다(DOC-87 §3-3). */
export function isJudgeParty(partyType: PartyType): boolean {
  return partyType === 'SUPERVISOR' || partyType === 'STAFF';
}

/**
 * 업체의 갈래 — 외주 · 구매 · 용역(V348 · DOC-96 §9). **업체의 속성**이다(사용자 결정 2026-09-29).
 * 로그인하면 서버가 세션에 실어 주고, 메뉴·홈 위젯이 이것으로 걸러진다. null 은 미지정 — 전부 보여 준다.
 */
export type VendorKind = 'SUBCONTRACT' | 'PURCHASE' | 'SERVICE';

export const VENDOR_KINDS: VendorKind[] = ['SUBCONTRACT', 'PURCHASE', 'SERVICE'];

export const VENDOR_KIND_LABEL: Record<VendorKind, string> = {
  SUBCONTRACT: '외주',
  PURCHASE: '구매',
  SERVICE: '용역',
};

export type ExternalSession = {
  id: string;
  name: string;
  email: string;
  partyType: PartyType;
  /** 소속. 협력사·감리단이면 `vendor_master` 의 이름, 직원이면 부서명이다. */
  partyName: string;
  /** 업체의 갈래. 직원·감리·미지정은 null. */
  /**
   * 업체가 맡는 <b>갈래들</b>(V364). 여럿일 수 있다 — 공사도 하고 자재도 대는 업체가 있다.
   * <b>비면 미지정</b>이고 그때는 전부 보여 준다(직원·감리도 빈 목록이다).
   */
  vendorKinds: VendorKind[];
  /** 견적·입찰에 부를 수 있는 업체인가(V364). 관문의 「견적·입찰」 칸이 이 값을 본다. */
  rfqEnabled: boolean;
  /** 배정된 현장. 종료일이 지난 배정은 서버가 빼고 준다. */
  sites: Array<{ id: string; name: string; readOnly: boolean }>;
};

export const sessionQueryKey = ['external', 'session'] as const;

/**
 * 지금 로그인한 사람.
 *
 * <p>토큰이 없으면 **묻지 않는다** — 서버에 401 을 받으러 갈 이유가 없다.
 * 401 은 재시도하지 않는다(토큰이 죽은 것이지 서버가 흔들린 것이 아니다).
 */
export function useSession() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: () => apiFetch<ExternalSession>('/auth/me'),
    enabled: readAccessToken() !== null,
    /*
      ★ <b>서버가 아직 옛 모양이어도 화면이 죽지 않게</b> 한 곳에서 메운다(2026-09-30).

      `vendorKinds`·`rfqEnabled` 는 V364 에서 생긴 칸이다. 화면을 먼저 배포하고 서버가 나중에 뜨는
      구간(개발 중 재시작 전이 늘 그렇다)에는 그 칸이 <b>없는 응답</b>이 온다. 그때 `vendorKinds.length`
      한 줄이 TypeError 를 내고 <b>화면이 통째로 흰 판</b>이 된다 — 로그인은 됐는데 아무것도 안 보인다.

      읽는 자리마다 `?? []` 를 흩뿌리지 않고 여기서 한 번 메운다. 타입은 있는 그대로 두고(서버는
      늘 준다), 없을 때의 값만 정한다 — 갈래는 빈 목록(미지정 = 전부 보임), 견적은 꺼짐이다.
    */
    select: (raw): ExternalSession => ({
      ...raw,
      vendorKinds: raw.vendorKinds ?? [],
      rfqEnabled: raw.rfqEnabled ?? false,
    }),
    retry: (failureCount, error) => {
      if (error instanceof ApiRequestError && error.status === 401) return false;
      return failureCount < 2;
    },
    staleTime: 60_000,
  });
}

export type LoginResult = {
  accessToken: string;
  refreshToken: string | null;
};

/**
 * 문 하나. 협력사·감리·직원을 화면이 가르지 않는다 — 서버가 계정을 보고 가른다(사용자 지시 2026-09-29 ·
 * `ExternalAuthService.loginAny`: 외부 계정 먼저, 안 맞으면 직원). DOC-87 §3-8 의 두 칸은 걷었다.
 */
export async function login(email: string, password: string): Promise<void> {
  const result = await apiFetch<LoginResult>('/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  storeTokens(result.accessToken, result.refreshToken);
}

/**
 * 초대 링크를 열었을 때 보이는 것. 업체 이름과 이메일까지만이다 — 아직 인증되지 않은 사람이
 * 보는 화면이라 현장·담당자 이름은 실리지 않는다.
 */
export type InvitePreview = {
  email: string | null;
  name: string | null;
  partyName: string | null;
  valid: boolean;
  reason: string | null;
};

export function previewInvite(token: string): Promise<InvitePreview> {
  return apiFetch<InvitePreview>(`/auth/invites/${encodeURIComponent(token)}`);
}

/** 초대 수락 — 계정이 여기서 서고, 곧바로 로그인된 상태가 된다. */
export async function acceptInvite(
  token: string,
  body: { password: string; name: string | null; phone: string | null },
): Promise<void> {
  const result = await apiFetch<LoginResult>(`/auth/invites/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body,
  });
  storeTokens(result.accessToken, result.refreshToken);
}

/**
 * 협력사 등록 신청서 — 로그인 앞의 세 번째 칸(DOC-96 §2-1). 비밀번호 칸이 없다: 계정은 본사가
 * 승인한 뒤 초대 링크로 선다. 사업자번호는 숫자 열 자리로 보낸다(화면이 하이픈을 벗긴다).
 */
export type VendorApplicationSubmit = {
  /** 첫 화면의 칸 셋 중 어디로 들어왔는가. 승인이 마스터로 옮긴다. */
  vendorKind: VendorKind;
  name: string;
  bizNo: string;
  ceoName: string;
  /** yyyy-MM-dd. 국세청 진위확인이 개업일자를 요구한다. */
  openedOn: string;
  mainTrade: string;
  bizType: string | null;
  bizClass: string | null;
  address: string | null;
  phone: string | null;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  note: string | null;
};

/**
 * 접수 — `POST /auth/applications` (multipart). 응답은 비어 있다. 실패는 종류를 가리지 않고
 * 한 문장이다 — 사유를 갈라 보여 주면 그것이 곧 검사 규칙을 알려 준다.
 */
export async function submitApplication(body: VendorApplicationSubmit, cert: File): Promise<void> {
  const form = new FormData();
  form.append('application', new Blob([JSON.stringify(body)], { type: 'application/json' }));
  form.append('cert', cert, cert.name);
  await apiFetch<void>('/auth/applications', { method: 'POST', body: form });
}

/** 파트너 페이지 「문의하기」 — 외주구매팀 메일로 간다. 표에 쌓지 않고 회신은 메일에서. */
export type VendorInquirySubmit = {
  name: string;
  company: string | null;
  phone: string | null;
  email: string;
  message: string;
};

export async function submitInquiry(body: VendorInquirySubmit): Promise<void> {
  await apiFetch<void>('/auth/inquiries', { method: 'POST', body });
}

/**
 * 로그아웃.
 *
 * <p>서버에 먼저 알리고 지운다. 서버가 못 받아도 **로컬은 반드시 지운다** — 여기서 멈추면
 * 「로그아웃을 눌렀는데 그대로인」 상태가 남고, 공용 기기에서 그것이 제일 나쁘다.
 */
export async function logout(): Promise<void> {
  try {
    await apiFetch<void>('/auth/logout', { method: 'POST' });
  }
  catch {
    // 서버가 답하지 않아도 아래는 실행된다.
  }
  finally {
    clearTokens();
  }
}
